import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, AvatarProps, Dialog } from "@mui/material";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

import StorageApi, { StorageBuckets } from "api/storage";
import AppBar from "../AppBar";
import PageLoader from "../PageLoader";
import SlideUpTransition from "../SlideUpTransition";
import styles from "./asyncAvatar.module.scss";

/**
 * TODO
 *
 * - [avatarFile] and [avatarName] should be passed together
 * - when [loadOriginal] is passed, [avatarFile] and [avatarName]
 *   should also be passed
 */

export interface AsyncAvatarProps extends AvatarProps {
  avatarFile?: Blob;
  avatarName?: string | null;
  bucket: StorageBuckets;
  loadOriginal?: boolean;
  loadSmallSize?: boolean;
  placeholder: string;
  preview?: boolean;
}

function AsyncAvatar(props: AsyncAvatarProps) {
  const {
    avatarFile,
    avatarName,
    bucket,
    className,
    loadOriginal = false,
    loadSmallSize,
    placeholder,
    preview = true,
    ...rest
  } = props;
  const [open, setOpen] = useState(false);
  const [smallAvatar, setSmallAvatar] = useState<Blob | null>(null);
  const [originalImage, setOriginalImage] = useState<Blob | null>(null);
  const [isLoadingOriginal, setIsLoadingOriginal] = useState(false);
  const mounted = useRef<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const storageApi = useMemo(() => new StorageApi(), []);

  /**
   * Fetch the original size of the avatar, not the compressed one
   */
  const fetchOriginalSizeAvatar = useCallback(async () => {
    if (avatarName) {
      setIsLoadingOriginal(true);
      const { file } = await storageApi.getFile(avatarName, bucket);
      if (mounted.current) {
        setOriginalImage(file);
        setIsLoadingOriginal(false);
      }
    }
  }, [avatarName, bucket, storageApi]);

  /**
   * Fetch small size of the avatar
   */
  const fetchSmallAvatar = useCallback(async () => {
    if (avatarName && loadSmallSize) {
      const { file } = await storageApi.getFile(`s-${avatarName}`, bucket);

      setSmallAvatar(file);
    }
  }, [avatarName, bucket, loadSmallSize, storageApi]);

  /**
   * Handle profile image click
   */
  const handleAvatarClick = async (e: any) => {
    if (avatarName) {
      e.stopPropagation();
      setOpen(true);
      navigate(location.pathname);

      await fetchOriginalSizeAvatar();
    }
  };

  const getImageUrl = () => {
    if (originalImage) {
      return URL.createObjectURL(originalImage);
    }
    if (avatarFile) {
      return URL.createObjectURL(avatarFile);
    }
    if (smallAvatar) {
      return URL.createObjectURL(smallAvatar);
    }

    return undefined;
  };

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setOpen(false);
    }
  }, [navigationType]);

  useEffect(() => {
    if (loadOriginal) {
      fetchOriginalSizeAvatar();
    }

    // Fetch small size avatar only if [avatarFile] was not provided
    if (!avatarFile) {
      fetchSmallAvatar();
    }
  }, [avatarFile, fetchOriginalSizeAvatar, fetchSmallAvatar, loadOriginal]);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  });

  return (
    <>
      <Avatar
        onClick={preview && ((avatarFile && avatarName) || smallAvatar) ? handleAvatarClick : undefined}
        className={classNames(className, {
          [styles.previewAvatar]: preview && (avatarFile || smallAvatar),
        })}
        src={getImageUrl()}
        {...rest}
      >
        {avatarFile ? null : placeholder}
      </Avatar>
      <Dialog
        fullScreen
        open={open}
        TransitionComponent={SlideUpTransition}
        className={styles.dialog}
        onClick={(e: any) => e.stopPropagation()}
      >
        <AppBar title='Preview avatar' noRadius />

        {isLoadingOriginal ? (
          <PageLoader />
        ) : (
          <div className={styles.imageContainer}>
            <img
              src={originalImage ? URL.createObjectURL(originalImage) : undefined}
              alt='Avatar'
              className={styles.image}
            />
          </div>
        )}
      </Dialog>
    </>
  );
}

export default AsyncAvatar;
